import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { UserLink } from '@/components/UserLink'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { LINK_CLASS } from '@/components/ui/Code'
import { QueryTable, type DataColumn } from '@/components/ui/DataTable'
import { EmptyState } from '@/components/ui/EmptyState'
import { FilterBar, type ActiveFilter } from '@/components/ui/FilterBar'
import { FilterChip } from '@/components/ui/FilterChip'
import { PageHeader } from '@/components/ui/PageHeader'
import { Segmented } from '@/components/ui/Segmented'
import { api } from '@/lib/api'
import { fmtInt } from '@/lib/format'
import { fieldLabel, fieldValue } from '@/lib/labels'

interface EventRow {
  id: number
  user_id: number
  tg_id: number
  username: string | null
  event_type: string
  label: string
  meta: Record<string, unknown> | null
  created_at: string | null
}

interface EventType {
  type: string
  label: string
  count: number
  noise: boolean
}

const PERIODS: { hours: number; label: string }[] = [
  { hours: 1, label: 'Час' },
  { hours: 24, label: 'Сутки' },
  { hours: 24 * 7, label: 'Неделя' },
  { hours: 24 * 30, label: 'Месяц' },
]

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('ru-RU') : '—')

/** Compact «Поле: значение» rendering of the event payload (no raw JSON on screen): known
 *  fields get their Russian name and formatted value (WP-16, lib/labels), an unknown key stays
 *  as it is. */
export function describeMeta(meta: Record<string, unknown> | null): string {
  if (!meta) return ''
  const skip = new Set(['job_id', 'payment_id'])
  return Object.entries(meta)
    .filter(([k, v]) => !skip.has(k) && v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${fieldLabel(k).label}: ${fieldValue(k, v)}`)
    .join(', ')
}

export function Activity() {
  const [params0] = useSearchParams()
  const [hours, setHours] = useState(params0.get('tg_id') ? 24 * 30 : 24)
  const [tgId, setTgId] = useState(params0.get('tg_id') ?? '')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [hideNoise, setHideNoise] = useState(true)

  const types = useQuery({
    queryKey: ['event-types', hours],
    queryFn: () =>
      api.get<{ types: EventType[]; noise_retention_days: number }>(`/events/types?hours=${hours}`),
    refetchInterval: 30000,
  })
  const [offset, setOffset] = useState(0)
  const params = useMemo(() => {
    const p = new URLSearchParams({ hours: String(hours), limit: String(ACTIVITY_PAGE), offset: String(offset) })
    if (tgId.trim()) p.set('tg_id', tgId.trim())
    const chosen = [...selected]
    if (chosen.length) p.set('types', chosen.join(','))
    else if (hideNoise) {
      const quiet = (types.data?.types ?? []).filter((t) => !t.noise).map((t) => t.type)
      if (quiet.length) p.set('types', quiet.join(','))
    }
    return p.toString()
  }, [hours, tgId, selected, hideNoise, types.data, offset])
  // A filter change starts from the newest page again.
  useEffect(() => setOffset(0), [hours, tgId, selected, hideNoise])
  const feed = useQuery({
    queryKey: ['events', params],
    queryFn: () => api.get<{ items: EventRow[]; has_more?: boolean }>(`/events?${params}`),
    refetchInterval: offset === 0 ? 15000 : false, // an older page stays put while it is read
    placeholderData: keepPreviousData,
  })
  const shown = feed.data?.items.length ?? 0
  const more = feed.data?.has_more === true

  const toggle = (t: string) =>
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })

  const filtered = tgId.trim() !== '' || selected.size > 0
  const typeLabel = (t: string) => types.data?.types.find((x) => x.type === t)?.label ?? t
  const active: ActiveFilter[] = []
  if (tgId.trim()) active.push({ id: 'tg', label: `Пользователь: ${tgId.trim()}`, onRemove: () => setTgId('') })
  for (const t of selected) active.push({ id: `type-${t}`, label: typeLabel(t), onRemove: () => toggle(t) })

  const columns: DataColumn<EventRow>[] = [
    { id: 'when', header: 'Когда', type: 'date', cell: (e) => fmt(e.created_at), mobile: 'meta' },
    {
      id: 'user',
      header: 'Пользователь',
      type: 'text',
      mobile: 'meta',
      cell: (e) => (
        <span className="inline-flex flex-wrap items-baseline gap-x-2">
          <button
            type="button"
            className={`tnum text-caption ${LINK_CLASS}`}
            data-raw
            title="Показать события этого пользователя"
            onClick={() => setTgId(String(e.tg_id))}
          >
            {e.tg_id}
          </button>
          {e.username && <span className="text-caption text-ink-3">@{e.username}</span>}
          <UserLink id={e.user_id} label="карточка" />
        </span>
      ),
    },
    { id: 'event', header: 'Событие', type: 'name', cell: (e) => e.label, mobile: 'title' },
    { id: 'details', header: 'Подробности', type: 'text', cell: (e) => <span className="text-caption">{describeMeta(e.meta)}</span> },
  ]

  return (
    <div>
      <PageHeader
        title="Активность пользователей"
        description="Что делают пользователи в боте и Mini App: команды, кнопки, генерации, оплаты, промокоды, бонусы. Обновляется каждые 15 секунд."
        detailsLabel="Что и сколько хранится"
        details={`Тексты промптов и адреса выплат не пишутся. Команды, кнопки и свободный текст хранятся ${
          types.data?.noise_retention_days ? `${types.data.noise_retention_days} дн.` : 'бессрочно'
        }, остальные события — бессрочно.`}
      />

      <FilterBar
        search={{
          value: tgId,
          onChange: setTgId,
          label: 'Telegram ID пользователя',
          placeholder: 'Все пользователи',
        }}
        active={active}
        onReset={() => {
          setTgId('')
          setSelected(new Set())
        }}
      >
        <Segmented
          label="Период"
          value={String(hours)}
          options={PERIODS.map((p) => ({ value: String(p.hours), label: p.label }))}
          onChange={(v) => setHours(Number(v))}
        />
        <Checkbox
          className="h-10"
          label="Скрыть команды и кнопки"
          checked={hideNoise}
          onChange={(on) => setHideNoise(on)}
        />
      </FilterBar>
      <div className="-mt-1 mb-4 flex flex-wrap gap-2" role="group" aria-label="Типы событий">
        {(types.data?.types ?? [])
          .filter((t) => t.count > 0 || selected.has(t.type))
          .map((t) => (
            <FilterChip key={t.type} pressed={selected.has(t.type)} onChange={() => toggle(t.type)} count={t.count}>
              {t.label}
            </FilterChip>
          ))}
      </div>

      <QueryTable
        label="События"
        scope="activity"
        query={feed}
        rows={(d) => d.items}
        columns={columns}
        getRowId={(e) => String(e.id)}
        errorTitle="Не удалось загрузить события"
        empty={
          filtered ? (
            <EmptyState
              title="Ничего не найдено"
              text="За этот период нет событий с выбранными типами или от этого пользователя."
              action={{
                label: 'Сбросить фильтры',
                onClick: () => {
                  setTgId('')
                  setSelected(new Set())
                },
              }}
            />
          ) : (
            <EmptyState
              title="Событий за период нет"
              text="Возьмите период длиннее."
              action={hours < 24 * 30 ? { label: 'Показать месяц', onClick: () => setHours(24 * 30) } : undefined}
            />
          )
        }
      />
      {(offset > 0 || more) && (
        <nav aria-label="Страницы событий" className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - ACTIVITY_PAGE))}>
            Новее
          </Button>
          <span className="tnum text-caption text-ink-3" aria-live="polite">
            Показаны события {fmtInt(offset + 1)}–{fmtInt(offset + shown)}
            {more ? ', показаны не все: есть более ранние' : ', это все за период'}
            {feed.isFetching ? ', обновляю…' : ''}
          </span>
          <Button variant="secondary" size="sm" disabled={!more} onClick={() => setOffset(offset + ACTIVITY_PAGE)}>
            Раньше
          </Button>
        </nav>
      )}
    </div>
  )
}

/** Events per page of the feed (the server answers `has_more`: one row beyond the page). */
export const ACTIVITY_PAGE = 200
